
const BREAKPOINTS = { lg: 1279, md: 768 };
const DRAG_THRESHOLD = 100;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const isCompact = () => document.documentElement.clientWidth < BREAKPOINTS.md;

const getItemsToShow = (root) => {
    const width = document.documentElement.clientWidth;
    if (isCompact()) {
        return 1;
    }
    const found = ["lg", "md"].find((key) => BREAKPOINTS[key] <= width);
    return Number(root.dataset[`items${found[0].toUpperCase()}${found.slice(1)}`]) || 1;
};

const initCarousel = (root) => {
    const slider = root.querySelector("[data-carousel-slider]");
    const track = root.querySelector("[data-carousel-track]");
    const items = Array.from(track.querySelectorAll(":scope > [data-carousel-item]"));
    const footer = root.querySelector("[data-carousel-footer]");
    const counter = root.querySelector("[data-carousel-counter]");
    const dots = root.querySelector("[data-carousel-dots]");
    const prev = root.querySelector("[data-carousel-prev]");
    const next = root.querySelector("[data-carousel-next]");
    const itemCount = items.length;

    let activeSlide = 0;
    let itemsToShow = 1;
    let slidesTotal = 1;
    let baseOffset = 0;
    let dragStart = null;
    let dragDelta = 0;

    const isRtl = () => getComputedStyle(root).direction === "rtl";

    const visibleStart = () => Math.min(activeSlide * itemsToShow, Math.max(itemCount - itemsToShow, 0));

    const setTransform = (px) => {
        track.style.transform = `translateX(${px}px)`;
    };

    const setDisabled = (button, disabled) => {
        button.disabled = disabled;
        button.classList.toggle("action--disabled", disabled);
    };

    const render = () => {
        const start = visibleStart();
        baseOffset = items[0].offsetLeft - items[start].offsetLeft;
        setTransform(baseOffset);

        // Off-page slides are hidden and inert (Elements CarouselItem), so tabbing skips them
        items.forEach((item, index) => {
            const visible = index >= start && index < start + itemsToShow;
            item.setAttribute("aria-hidden", String(!visible));
            item.inert = !visible;
        });

        footer.hidden = slidesTotal <= 1;
        counter.textContent = `${activeSlide + 1}/${itemCount}`;
        dots.querySelectorAll("[data-carousel-dot]").forEach((dot, index) => {
            const selected = index === activeSlide;
            dot.classList.toggle("nova-carousel__dot--active", selected);
            dot.setAttribute("aria-current", String(selected));
        });
        setDisabled(prev, activeSlide === 0);
        setDisabled(next, activeSlide === slidesTotal - 1);
    };

    const goTo = (slide, { focus = false } = {}) => {
        if (slide < 0 || slide >= slidesTotal) {
            return;
        }
        activeSlide = slide;
        render();
        if (focus) {
            const target = items
                .filter((item) => item.getAttribute("aria-hidden") === "false")
                .map((item) => item.querySelector(FOCUSABLE))
                .find(Boolean);
            target?.focus();
        }
    };

    const buildDots = () => {
        dots.replaceChildren();
        for (let index = 0; index < slidesTotal; index++) {
            const dot = document.createElement("div");
            dot.className = "nova-carousel__dot";
            dot.setAttribute("role", "button");
            dot.setAttribute("tabindex", "0");
            dot.setAttribute("aria-label", `Slide ${index + 1}`);
            dot.dataset.carouselDot = "";
            dot.addEventListener("click", () => goTo(index));
            dot.addEventListener("keydown", (event) => {
                if ([" ", "Enter", "Spacebar"].includes(event.key)) {
                    event.preventDefault();
                    goTo(index);
                }
            });
            dots.appendChild(dot);
        }
    };

    const layout = () => {
        itemsToShow = getItemsToShow(root);
        slidesTotal = Math.max(Math.ceil(itemCount / itemsToShow), 1);
        // One item per view shows 75% of a slide so the next one peeks (Elements itemWidth)
        root.style.setProperty("--nova-carousel-items", itemsToShow);
        root.classList.toggle("nova-carousel--single-item", itemsToShow === 1);
        // md and below show the "n/total" counter instead of dots
        root.classList.toggle("nova-carousel--compact", isCompact());
        activeSlide = Math.min(activeSlide, slidesTotal - 1);
        buildDots();
        render();
    };

    prev.addEventListener("click", () => goTo(activeSlide - 1));
    next.addEventListener("click", () => goTo(activeSlide + 1));

    // Arrow keys from inside a slide; RTL swaps the direction
    items.forEach((item) => {
        item.addEventListener("keydown", (event) => {
            if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") {
                return;
            }
            event.preventDefault();
            const forward = (event.key === "ArrowRight") !== isRtl();
            goTo(activeSlide + (forward ? 1 : -1), { focus: true });
        });
    });

    // Mouse/touch drag: past 100px moves one page, otherwise snaps back
    const pointerX = (event) => (event.touches ? event.touches[0].clientX : event.clientX);
    const dragBegin = (event) => {
        dragStart = pointerX(event);
        dragDelta = 0;
        track.classList.add("nova-carousel__track--dragging");
    };
    const dragMove = (event) => {
        if (dragStart === null) {
            return;
        }
        dragDelta = pointerX(event) - dragStart;
        setTransform(baseOffset + dragDelta);
    };
    const dragFinish = () => {
        if (dragStart === null) {
            return;
        }
        dragStart = null;
        track.classList.remove("nova-carousel__track--dragging");
        // Dragging towards the end (left in LTR, right in RTL) moves forward
        const forward = isRtl() ? dragDelta > DRAG_THRESHOLD : dragDelta < -DRAG_THRESHOLD;
        const back = isRtl() ? dragDelta < -DRAG_THRESHOLD : dragDelta > DRAG_THRESHOLD;
        if (forward && activeSlide + 1 < slidesTotal) {
            goTo(activeSlide + 1);
        } else if (back && activeSlide > 0) {
            goTo(activeSlide - 1);
        } else {
            render();
        }
    };

    slider.addEventListener("mousedown", dragBegin);
    slider.addEventListener("touchstart", dragBegin, { passive: true });
    slider.addEventListener("mousemove", dragMove);
    slider.addEventListener("touchmove", dragMove, { passive: true });
    slider.addEventListener("mouseup", dragFinish);
    slider.addEventListener("mouseleave", dragFinish);
    slider.addEventListener("touchend", dragFinish);
    // Stop native image/link drag from hijacking the swipe
    slider.addEventListener("dragstart", (event) => event.preventDefault());
    // A real drag shouldn't also click the card link underneath
    slider.addEventListener("click", (event) => {
        if (Math.abs(dragDelta) > 5) {
            event.preventDefault();
            event.stopPropagation();
        }
    }, true);

    let resizeFrame;
    window.addEventListener("resize", () => {
        cancelAnimationFrame(resizeFrame);
        resizeFrame = requestAnimationFrame(layout);
    });

    layout();
};

export default function carousel() {
    document.querySelectorAll("[data-nova-carousel]").forEach((root) => {
        if (root.dataset.carouselReady || !root.querySelector("[data-carousel-item]")) {
            return;
        }
        root.dataset.carouselReady = "true";
        initCarousel(root);
    });
}
